import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import TooltipTitle from '@/components/TooltipTitle'
import useStakingBot from '@/hooks/useStakingBot'
import { processBalance, ProcessedBalance } from '@/utils/utils'

interface CollectProps {
  close: () => void
  claimableRewards: ProcessedBalance
  ll1Withdrawable: ProcessedBalance
  rl1Withdrawable: ProcessedBalance
}

const Collect: React.FC<CollectProps> = ({
  close,
  claimableRewards,
  ll1Withdrawable,
  rl1Withdrawable,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()

  // Connect to staking bot
  const { canCollect, error, collectRewards } = useStakingBot()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isReady, setIsReady] = useState<boolean>(false)

  const handleClaim = useCallback(async () => {
    setIsLoading(true)
    await collectRewards()
    setIsLoading(false)
    setIsReady(true)
  }, [collectRewards])

  const totalUnwrap = useMemo(
    () => processBalance(ll1Withdrawable.value + rl1Withdrawable.value),
    [ll1Withdrawable, rl1Withdrawable]
  )

  return (
    <>
      {error.isError && (
        <ErrorMessage
          message={t(
            'components.staking.staking.modal.claim.errorMessage',
            'Error Collecting Rewards'
          )}
          details={error.msg}
        />
      )}
      {isLoading && (
        <ConfirmationLoading
          title={t(
            'components.staking.staking.modal.claim.loadingMessage',
            'Validating Collection'
          )}
        />
      )}
      {isReady && !isLoading && !error.isError && (
        <ConfirmationReady
          message={t('components.staking.staking.modal.claim.readyMessage', 'Rewards Collected')}
          tip={t(
            'components.staking.staking.modal.claim.tipMessage',
            'Check your Wallet to see the Transaction status and to unlock Rewards L1 (RL1) into liquid L1 over time.'
          )}
          success={close}
        />
      )}
      {!isReady && !isLoading && !error.isError && (
        <Stack>
          <Text style={{ marginBottom: '20px' }}>
            {t(
              'components.staking.staking.modal.claim.info',
              'Review the Liquid L1 and Rewards L1 available to you, then press ‘Collect’ to bring those tokens into your Wallet.'
            )}
          </Text>
          <Stack gap="xs" style={{ flexDirection: 'column', paddingBottom: 4 }}>
            <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <TooltipTitle
                title={t('components.staking.staking.modal.claim.claimable', 'Rewards L1')}
                tooltip={t(
                  'components.staking.staking.modal.claim.claimableTooltip',
                  'Rewards L1 (RL1) will unlock into useable L1 in your Wallet over time.'
                )}
              />
              <Title
                order={4}
                style={{
                  color: theme.colors.paragraph[0],
                  textAlign: 'right',
                }}
              >
                {claimableRewards.display} RL1
              </Title>
            </Stack>
            <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <TooltipTitle
                title={t('components.staking.staking.modal.claim.available', 'Liquid L1')}
                tooltip={t(
                  'components.staking.staking.modal.claim.availableTooltip',
                  'Liquid L1 that has unlocked from RL1 or LL1 rewards and is now useable currency on the LAMINA1 network.'
                )}
              />
              <Title
                order={4}
                style={{
                  color: theme.colors.paragraph[0],
                  textAlign: 'right',
                }}
              >
                {totalUnwrap.display} L1
              </Title>
            </Stack>
          </Stack>
          <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
            <RootButton onClick={close} secondary style1={false} expand>
              {t('buttons.cancel', 'Cancel')}
            </RootButton>
            <RootButton disabled={!canCollect} onClick={handleClaim} style1={false} expand>
              {t('buttons.collect', 'Collect')}
            </RootButton>
          </Stack>
        </Stack>
      )}
    </>
  )
}
export default Collect
